import React, { useEffect, useContext } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import { UserContext } from '../components/UserProvider';
import Error from '../components/Error';
import Loader from '../components/Loader';
import { Router } from '../routes.mjs';
import { saveRouterState } from './routerState';
import { hasGlobalRole, hasRoleInAnyGroup } from './hasUserRoles';

const wrappedComponentPropertyTypes = {
  requiredRoles: PropTypes.shape({
    globalRolesAll: PropTypes.arrayOf(
      PropTypes.string.isRequired,
    ),
    globalRolesAny: PropTypes.arrayOf(
      PropTypes.string.isRequired,
    ),
    scopedRolesAll: PropTypes.arrayOf(
      PropTypes.string.isRequired,
    ),
    scopedRolesAny: PropTypes.arrayOf(
      PropTypes.string.isRequired,
    ),
  }).isRequired,
};

type WrappedComponentProperties = InferProps<
  typeof wrappedComponentPropertyTypes
>;

const withRestrictedAccess = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>,
) => {
  const WrappedComponent = ({
    requiredRoles,
    ...properties
  }: WrappedComponentProperties & Record<string, unknown>) => {
    const {
      globalRolesAll,
      globalRolesAny,
      scopedRolesAll,
      scopedRolesAny,
    } = requiredRoles;

    const router = useRouter();
    const { user, loading, roles } = useContext(UserContext);

    useEffect(() => {
      if (!user && !loading) {
        saveRouterState(router);
        Router.replaceRoute('login');
      }
    }, [router, user, loading]);

    if (loading) return <Loader />;

    const userRoles = roles ?? {};

    if (
      (globalRolesAll && globalRolesAll.some(
        (role) => !hasGlobalRole(userRoles, role),
      ))
      || (globalRolesAny && globalRolesAny.every(
        (role) => !hasGlobalRole(userRoles, role),
      ))
      || (scopedRolesAll && scopedRolesAll.some(
        (role) => !hasRoleInAnyGroup(userRoles, role),
      ))
      || (scopedRolesAny && scopedRolesAny.every(
        (role) => !hasRoleInAnyGroup(userRoles, role),
      ))
    ) {
      return <Error error="access denied" />;
    }

    return (
      <Component {...properties} />
    );
  };

  WrappedComponent.propTypes = wrappedComponentPropertyTypes;

  return WrappedComponent;
};

export default withRestrictedAccess;
